//@ts-nocheck

'use client'

import React from 'react'

import * as S from './MenuContent.styles'

import { motion, AnimatePresence } from 'motion/react'
import MenuItem from '../MenuItem/MenuItem.component'
import MenuHighlight from '../MenuHighlight/MenuHighlight.component'
import {
  Container,
  Row,
  Col,
  SpacerBase,
  Visible,
} from '../../../atoms/GridSystem'
import { Paragraph } from '../../../atoms/Typography/Typography.component'
import Button from '../../../atoms/Button/Button.component'
import useMediaQuery from '../../../../hooks/useMediaQuery'
import { useTranslations } from 'next-intl'

interface MenuItemInterface {
  id?: string
  label?: string
  description?: string
  href?: string
  [key: string]: any
}

interface MenuData {
  tagline: string
  items: MenuItemInterface[]
  highlight?: {
    preTitle?: string | undefined
    label?: string | undefined
    description?: string | undefined
    cta?: {
      href: string
      label: string
    }
  }
}

interface MenuContentProps {
  data: MenuData[]
  isOpen: boolean
  submenu: string
  handleGoBack: () => void
  handleCloseMenu: () => void
}

type MenuContentLabels = {
  close: string
  closeSubmenu: string
}

const groupItemsInTwo = (items: MenuItem[]): MenuItem[][] => {
  let arr: MenuItem[][] = []

  for (let i = 0; i < 6; i += 2) {
    const firstItem = items[i]
    const secondItem = items[i + 1]
    const group = [firstItem, secondItem].filter(Boolean) as MenuItem[]

    arr.push(group)
  }

  return arr
}

const renderMenuContent = (
  data: MenuData,
  handleGoBack: () => void,
  handleCloseMenu: () => void,
  labels: MenuContentLabels
) => {
  const groupedItems = groupItemsInTwo(data.items)
  const highlight = data.highlight ? data.highlight : null

  return (
    <S.MenuContentInner>
      <Container>
        <Row>
          <Col xs={12} lg={8}>
            <Visible breakpoints={['lg', 'xl', 'xxl', 'xxxl']}>
              <Paragraph content={data.tagline} typo={'displayMD'} />
            </Visible>
            <Visible breakpoints={['sm', 'md']}>
              <S.MenuContentGoBack>
                <Button
                  label={data.tagline}
                  variant="secondary"
                  size="small"
                  iconStart="chevron-left"
                  handleClick={() => handleGoBack()}
                />
              </S.MenuContentGoBack>
            </Visible>
            <SpacerBase lg={4} />
            <Row>
              {groupedItems.map((group, index) => (
                <Col key={index} xs={12} md={12} lg={6} xl={4}>
                  {group.map((item) => {
                    if (!item) return null
                    return <MenuItem key={item.id} {...item} />
                  })}
                </Col>
              ))}
            </Row>
          </Col>
          <Col xs={12} md={12} lg={4}>
            {highlight ? (
              <MenuHighlight
                preTitle={highlight.preTitle}
                title={highlight.title}
                description={highlight.description}
                cta={highlight.cta}
              />
            ) : null}
          </Col>
          <Col xs={12} $justify={'flex-end'}>
            <S.MenuCloseButtonContainer>
              <Button
                ariaLabel={labels.closeSubmenu}
                label={labels.close}
                variant="secondary"
                size="small"
                iconStart="x-mark"
                handleClick={handleCloseMenu}
              />
            </S.MenuCloseButtonContainer>
          </Col>
        </Row>
      </Container>
    </S.MenuContentInner>
  )
}

const MenuContent = ({
  data,
  isOpen,
  submenu,
  handleGoBack,
  handleCloseMenu,
}: MenuContentProps) => {
  const t = useTranslations('header')
  const dataToShow = data.find((item) => item.tagline === submenu)
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const motionConfig = {
    initial: isMobile ? { opacity: 0 } : { y: 10, opacity: 1 },
    animate: isMobile ? { opacity: 1 } : { y: 0, opacity: 1 },
    exit: isMobile ? { opacity: 0 } : { y: -10, opacity: 0 },
    transition: { duration: 0.2 },
  }

  return dataToShow ? (
    <S.MenuContent isOpen={isOpen}>
      <AnimatePresence mode={'wait'}>
        <motion.div {...motionConfig} key={`${submenu}-${isOpen}`}>
          {renderMenuContent(dataToShow, handleGoBack, handleCloseMenu, {
            close: t('close'),
            closeSubmenu: t('closeSubmenu'),
          })}
        </motion.div>
      </AnimatePresence>
    </S.MenuContent>
  ) : null
}

export default MenuContent
